import Modal from "@/components/shared/Modal.tsx";
import { FormProvider, useForm } from "react-hook-form";
import TextInput from "@/components/shared/form/fields/TextInput.tsx";
import FormActions from "@/components/shared/form/fields/FormActions.tsx";
import PageLoading from "@/components/shared/PageLoading.tsx";
import { useInvite, useRenewInvite } from "./api";

type RenewInviteModalArgs = {
  inviteId: string;
  setShowModal: (showModal: boolean) => void;
  onSuccess?: () => void;
};

export default function RenewInviteModal({
  inviteId,
  setShowModal,
  onSuccess,
}: RenewInviteModalArgs) {
  const { data: invite, isLoading } = useInvite(inviteId);
  const { mutate: renewInvite } = useRenewInvite(
    inviteId,
    invite?.type || "ExternalInvite",
    {
      onSuccess: () => {
        setShowModal(false);
        toastr.success(I18n.t("js.invites.renew.success_flash"));
        onSuccess?.();
      },
      onError: () => {
        toastr.error(I18n.t("js.invites.renew.not_renewable_flash"));
      },
    },
  );
  const methods = useForm<{ body: string }>({
    values: { body: invite?.body || "" },
  });

  function validate(data: { body: string }) {
    if (invite?.type === "ExternalInvite" && !data.body.match(/\$LINK/)) {
      toastr.error(I18n.t("js.invites.validation.link_in_body"));
      return false;
    }
    return true;
  }

  function submit(data: { body: string }) {
    if (!validate(data)) return;

    const formData = new FormData();
    formData.append("body", data.body);

    renewInvite(formData);
  }

  return (
    <Modal
      title={I18n.t("js.invites.actions.renew_invite")}
      onClose={() => setShowModal(false)}
    >
      {isLoading ? (
        <PageLoading />
      ) : (
        <FormProvider {...methods}>
          <form
            className="flex flex-col gap-5 min-h-[368px]"
            onSubmit={methods.handleSubmit(submit)}
          >
            <TextInput
              name="body"
              label={I18n.t(
                `js.invites.renew.${(invite?.type || "externalinvite").toLowerCase()}.body_label`,
              )}
              rows={10}
              multiline
            />
            <FormActions
              saveText={I18n.t("js.invites.renew.submit")}
              onCancel={() => setShowModal(false)}
            />
          </form>
        </FormProvider>
      )}
    </Modal>
  );
}
